import { ExpectedBooking } from '@blisspointmedia/bpm-types/dist/ExpectedBookings';
import * as Dfns from 'date-fns/fp';
import ExcelJS from 'exceljs';
import * as R from 'ramda';
import { excelNumberToDate } from '@blisspointmedia/bpm-types/dist/excelUtils';
import { formatDecimal } from '@blisspointmedia/bpm-types/dist/formatUtils';
import { SelectorOption } from '@Components/ModalEditWidget/modal-edit-constants';
import { getFormattedValue } from '@Components/NumberFormatter/NumberFormatter';
import { cellToPrimitiveKeepDates, sheetToJson } from '@src/utils/exceljs-utils';
import {
  DATA_VALIDATION_ROWS,
  DECIMAL_GOAL_NAMES,
  FIELD_TO_DISPLAY_LABEL,
  FIELD_TO_OPTIONS_KEY,
  FIRST_OPTION_COLUMN,
  GoalRow,
  GoalSelectorOptions,
  KPI_OPTIONAL_GOAL_NAMES,
  MULTI_VALUE_COLUMN_KEYS,
  OptionsKey,
  PACING_CHANNELS
} from './goal-intake-constants';

const transformKeyToTitleCase = (key: string): string =>
  key
    .split('_')
    .map((word, index) =>
      index === 0 ? word.charAt(0).toUpperCase() + word.slice(1) : word.toLowerCase()
    )
    .join(' ');

const getDisplayLabel = (field: string): string =>
  FIELD_TO_DISPLAY_LABEL[field] ?? transformKeyToTitleCase(field);

export const requiresKpi = (data: Partial<GoalRow>): boolean =>
  Boolean(data.goal_name) && !KPI_OPTIONAL_GOAL_NAMES.has(String(data.goal_name));

export const isDecimalGoal = (goalName: unknown): boolean =>
  DECIMAL_GOAL_NAMES.has(String(goalName));

// Takes either a data-field key (e.g. campaign_platform) or an options key / segment name.
const isMultiValueColumn = (key: string): boolean =>
  MULTI_VALUE_COLUMN_KEYS.has(FIELD_TO_OPTIONS_KEY[key] ?? key);

const isEmptyValue = (value: unknown): boolean =>
  value == null || value === '' || (Array.isArray(value) && value.length === 0);

const joinArrayValue = (value: unknown): unknown =>
  Array.isArray(value) ? value.join(', ') : value;

const MAX_LISTED_OPTIONS = 20;

// Option lists can run to thousands of campaigns, so build each list's lookup Set once.
const allowedSetCache = new WeakMap<SelectorOption[], Set<unknown>>();
const getAllowedSet = (options: SelectorOption[]): Set<unknown> => {
  let allowed = allowedSetCache.get(options);
  if (!allowed) {
    allowed = new Set(options.map(o => o.value));
    allowedSetCache.set(options, allowed);
  }
  return allowed;
};

const listAllowedValues = (allowed: Set<unknown>): string => {
  const values = Array.from(allowed);
  const listed = values.slice(0, MAX_LISTED_OPTIONS).join(', ');
  const remaining = values.length - MAX_LISTED_OPTIONS;
  return remaining > 0 ? `${listed} and ${remaining} more` : listed;
};

const validateFieldAgainstOptions = (
  field: string,
  value: unknown,
  optionsKey: string,
  selectorOptions: GoalSelectorOptions
): string | null => {
  const options = selectorOptions[optionsKey];
  if (!options || options.length === 0) {
    return null;
  }
  const allowed = getAllowedSet(options);
  const values = Array.isArray(value) ? value : [value];
  const invalid = values.find(v => v != null && v !== '' && !allowed.has(v));
  if (invalid === undefined) {
    return null;
  }
  return `Invalid value "${invalid}" for ${getDisplayLabel(field)}. Allowed values: ${listAllowedValues(allowed)}`;
};

// KPI/source only apply to goals that pace on a KPI; any other goal's leftover values are ignored.
const withEffectiveKpi = <T extends Partial<GoalRow>>(data: T): T =>
  requiresKpi(data) ? data : { ...data, kpi: null, source: null };

// Two rows pace the same thing only when their KPI and source also match (empty and null are
// equivalent).
const sameKpiSource = (a: Partial<GoalRow>, b: Partial<GoalRow>): boolean => {
  const [effectiveA, effectiveB] = [withEffectiveKpi(a), withEffectiveKpi(b)];
  return (
    (effectiveA.kpi || null) === (effectiveB.kpi || null) &&
    (effectiveA.source || null) === (effectiveB.source || null)
  );
};

const isIsoDate = (value: string): boolean =>
  /^\d{4}-\d{2}-\d{2}$/.test(value) && Dfns.isValid(Dfns.parseISO(value));

interface GoalValidationResult {
  isValid: boolean;
  error?: string;
}

export const validateGoal = (
  data: Partial<GoalRow>,
  existingRows: GoalRow[],
  customSegments: string[],
  selectorOptions: GoalSelectorOptions
): GoalValidationResult => {
  const effective = withEffectiveKpi(data);
  const { id, start_date, end_date, goal_name, goal_value } = effective;

  // Unsaved rows have no id, so they can never be the row being edited.
  const conflictingGoal = existingRows.find(
    row =>
      (row.id == null || row.id !== id) &&
      new Date(String(row.start_date)) <= new Date(String(end_date)) &&
      new Date(String(row.end_date)) >= new Date(String(start_date)) &&
      row.goal_name === goal_name &&
      sameKpiSource(row, effective) &&
      customSegments.every(seg => R.equals(row[seg], effective[seg]))
  );

  if (conflictingGoal) {
    const formattedGoal = isDecimalGoal(goal_name)
      ? conflictingGoal.goal_value
      : getFormattedValue({ value: conflictingGoal.goal_value, type: '$', decimals: 2 });
    return {
      isValid: false,
      error: `A conflicting goal with a ${conflictingGoal.goal_name} of ${formattedGoal} already exists`
    };
  }

  if (
    !start_date ||
    !end_date ||
    !customSegments.some(segment => !isEmptyValue(effective[segment])) ||
    !goal_name ||
    !goal_value
  ) {
    return {
      isValid: false,
      error: 'Start Date, End Date, segments, Goal Name and Goal Value cannot be empty'
    };
  }

  if (![start_date, end_date].every(isIsoDate)) {
    return { isValid: false, error: 'Start Date and End Date must be dates formatted yyyy-MM-dd' };
  }

  if (requiresKpi(effective) && (isEmptyValue(effective.kpi) || isEmptyValue(effective.source))) {
    return {
      isValid: false,
      error: `KPI and KPI Source are required for Goal Name "${goal_name}"`
    };
  }

  if (new Date(start_date) > new Date(end_date)) {
    return { isValid: false, error: 'End Date should not be before Start Date' };
  }

  const fieldsToCheck: [string, string][] = [
    ...Object.entries(FIELD_TO_OPTIONS_KEY),
    ...customSegments.map((seg): [string, string] => [seg, seg])
  ];
  for (const [field, optionsKey] of fieldsToCheck) {
    const optionError = validateFieldAgainstOptions(
      field,
      effective[field],
      optionsKey,
      selectorOptions
    );
    if (optionError) {
      return { isValid: false, error: optionError };
    }
  }

  return { isValid: true };
};

// Shapes a row the way setBudgetIntakeData expects it: empty segments are left out, empty
// multi-selects become null, and KPI/source are dropped for goals that don't pace on a KPI so a
// stale value from before a goal-type change is never saved.
export const toSaveRow = (
  row: GoalRow,
  { company, email, customSegments }: { company: string; email: string; customSegments: string[] }
): GoalRow => {
  const segments = Object.fromEntries(
    customSegments
      .filter(segment => !isEmptyValue(row[segment]))
      .map(segment => [segment, row[segment]])
  );
  const keepKpi = requiresKpi(row);
  return {
    id: row.id,
    company,
    start_date: row.start_date,
    end_date: row.end_date,
    ...segments,
    goal_name: row.goal_name,
    goal_value: row.goal_value,
    lastuser: email,
    lastmodified: row.lastmodified,
    source: keepKpi ? row.source : undefined,
    kpi: keepKpi ? row.kpi : undefined,
    campaign_platform: isEmptyValue(row.campaign_platform) ? null : row.campaign_platform,
    ad_group_platform: isEmptyValue(row.ad_group_platform) ? null : row.ad_group_platform
  };
};

export const buildGoalsWorkbook = (
  rows: GoalRow[],
  company: string,
  customSegments: string[],
  selectorOptions: GoalSelectorOptions
): ExcelJS.Workbook => {
  const workbook = new ExcelJS.Workbook();
  const worksheet = workbook.addWorksheet(company);

  const segmentColumns = customSegments.map(key => ({
    header: transformKeyToTitleCase(key),
    key
  }));
  // Every column after id / Start Date / End Date, in sheet order. Paired with the options key
  // each column's dropdown draws from (goal_value has none).
  const optionColumns = [
    ...segmentColumns.map(column => ({ ...column, optionsKey: column.key })),
    { header: 'Goal Name', key: 'goal_name', optionsKey: OptionsKey.GOAL_NAME },
    { header: 'Goal Value', key: 'goal_value', optionsKey: null },
    { header: 'KPI', key: 'kpi', optionsKey: OptionsKey.KPI },
    { header: 'KPI Source', key: 'source', optionsKey: OptionsKey.SOURCE },
    { header: 'Campaign (Platform)', key: 'campaign_platform', optionsKey: OptionsKey.CAMPAIGN },
    { header: 'Ad Group (Platform)', key: 'ad_group_platform', optionsKey: OptionsKey.AD_GROUP }
  ];

  worksheet.columns = [
    { header: 'id', key: 'id' },
    { header: 'Start Date', key: 'start_date' },
    { header: 'End Date', key: 'end_date' },
    ...optionColumns.map(({ header, key }) => ({ header, key }))
  ];

  worksheet.addRows(
    rows.map(row => ({
      id: row.id,
      company: row.company,
      start_date: row.start_date,
      end_date: row.end_date,
      ...Object.fromEntries(customSegments.map(key => [key, joinArrayValue(row[key])])),
      goal_name: row.goal_name,
      goal_value: row.goal_value,
      source: row.source,
      kpi: row.kpi,
      campaign_platform: joinArrayValue(row.campaign_platform),
      ad_group_platform: joinArrayValue(row.ad_group_platform)
    }))
  );

  const listsWorksheet = workbook.addWorksheet('Lists');
  listsWorksheet.state = 'hidden';
  listsWorksheet.columns = optionColumns.map(({ header, key }) => ({ header, key }));

  optionColumns.forEach(({ key, optionsKey }, col) => {
    if (!optionsKey) {
      return;
    }
    const allowed = (selectorOptions[optionsKey] ?? []).map(o => o.value);
    const sheetColumn = col + FIRST_OPTION_COLUMN;

    if (allowed.length > 0) {
      ['', ...allowed].forEach((option, i) => {
        listsWorksheet.getRow(i + 2).getCell(col + 1).value = option;
      });
    }

    // Multi-value columns can't use a single-value dropdown; explain the format in a header note.
    if (isMultiValueColumn(key)) {
      worksheet.getRow(1).getCell(sheetColumn).note =
        'Multi-select. Comma-separate multiple values. Allowed values are on the hidden "Lists" sheet.';
      return;
    }
    if (allowed.length === 0) {
      return;
    }

    const listLength = allowed.length + 1;
    const { letter } = listsWorksheet.getColumn(col + 1);
    const formulaRange = `Lists!$${letter}$2:$${letter}$${listLength + 1}`;
    for (let row = 2; row < DATA_VALIDATION_ROWS; row++) {
      worksheet.getRow(row).getCell(sheetColumn).dataValidation = {
        type: 'list',
        allowBlank: true,
        formulae: [formulaRange]
      };
    }
  });

  return workbook;
};

const parseMultiValue = (rawValue: unknown): string[] | null => {
  if (Array.isArray(rawValue)) {
    return rawValue;
  }
  if (rawValue == null) {
    return null;
  }
  const parts = String(rawValue)
    .split(',')
    .map(part => part.trim().replace(/^"|"$/g, ''))
    .filter(part => part !== '');
  return parts.length > 0 ? parts : null;
};

// Looks a column up under its field key, its label, or the label lowercased (headers retyped by
// hand). sheetToJson already trims header whitespace.
const pickColumn = (row: Record<string, unknown>, field: string, label: string): unknown => {
  const candidates = [field, label, label.toLowerCase()];
  const key = candidates.find(candidate => row[candidate] != null && row[candidate] !== '');
  return key === undefined ? undefined : row[key];
};

// Spreadsheet cells come back as unknown; blank cells read as absent.
const optionalString = (value: unknown): string | undefined =>
  value == null || value === '' ? undefined : String(value);

const optionalNumber = (value: unknown): number | undefined =>
  value == null || value === '' ? undefined : Number(value);

const isOnlyDigits = (value: unknown): boolean => /^\d+$/.test(String(value));

const parseImportDate = (value: unknown): string | undefined => {
  if (value == null) {
    return undefined;
  }
  if (value instanceof Date) {
    return value.toISOString().slice(0, 10);
  }
  return isOnlyDigits(value) ? excelNumberToDate(Number(value)) : String(value);
};

export const mapImportRow = (
  row: Record<string, unknown>,
  company: string,
  customSegments: string[]
): GoalRow => {
  const sourceKey = Object.keys(row).find(key => /^(?:kpi)?[\s_-]?source$/i.test(key));
  const goalName = optionalString(pickColumn(row, 'goal_name', 'Goal Name'));
  const goalValue = pickColumn(row, 'goal_value', 'Goal Value');

  const segments = Object.fromEntries(
    customSegments.map(key => {
      const header = transformKeyToTitleCase(key);
      const rawValue = row[key] ?? row[header];
      if (rawValue == null) {
        return [key, null];
      }
      return [key, isMultiValueColumn(key) ? parseMultiValue(rawValue) : rawValue];
    })
  );

  return {
    id: optionalNumber(row.id ?? row.ID),
    company: row.company ? String(row.company) : company,
    start_date: parseImportDate(pickColumn(row, 'start_date', 'Start Date')),
    end_date: parseImportDate(pickColumn(row, 'end_date', 'End Date')),
    goal_name: goalName,
    // Dollar goals are stored to the cent; ratio/count goals keep their full precision.
    goal_value: isDecimalGoal(goalName)
      ? Number(goalValue)
      : Number(formatDecimal(Number(goalValue), 2)),
    lastmodified: row.lastmodified
      ? String(row.lastmodified)
      : Dfns.format('yyyy-MM-dd hh:mma', new Date()),
    kpi: optionalString(row.kpi ?? row.KPI),
    source: sourceKey ? optionalString(row[sourceKey]) : undefined,
    campaign_platform: parseMultiValue(pickColumn(row, 'campaign_platform', 'Campaign (Platform)')),
    ...segments
  };
};

const normalizeEmpty = (value: unknown): unknown => (isEmptyValue(value) ? null : value);

// Compares every field setBudgetIntakeData writes on update (it ignores ad_group_platform).
const rowIsDiff = (oldRow: GoalRow, newRow: GoalRow, customSegments: string[]): boolean => {
  const [oldEffective, newEffective] = [withEffectiveKpi(oldRow), withEffectiveKpi(newRow)];
  return (
    oldRow.company !== newRow.company ||
    oldRow.start_date !== newRow.start_date ||
    oldRow.end_date !== newRow.end_date ||
    [...customSegments, 'kpi', 'source', 'campaign_platform'].some(
      field => !R.equals(normalizeEmpty(oldEffective[field]), normalizeEmpty(newEffective[field]))
    ) ||
    oldRow.goal_name !== newRow.goal_name ||
    oldRow.goal_value !== newRow.goal_value
  );
};

const rowKey = (row: GoalRow, customSegments: string[]): string => {
  const effective = withEffectiveKpi(row);
  return [
    effective.start_date,
    effective.end_date,
    effective.goal_name,
    effective.goal_value,
    effective.kpi || null,
    effective.source || null,
    customSegments.map(k => JSON.stringify(effective[k] ?? null)).join('|')
  ].join('||');
};

// Parses a bulk-upload workbook into the rows that actually change something: new rows (no id)
// and existing rows whose values differ. Exact duplicates within the sheet are collapsed. Each row
// is checked for conflicts against the saved rows plus the sheet rows accepted before it. Throws
// with the offending spreadsheet row number on the first row that fails validation.
export const processBulkImport = async (
  file: ArrayBuffer,
  existingRows: GoalRow[],
  company: string,
  customSegments: string[],
  selectorOptions: GoalSelectorOptions
): Promise<GoalRow[]> => {
  const existingById = new Map(
    existingRows.filter(row => row.id != null).map(row => [row.id, row])
  );

  const workbook = new ExcelJS.Workbook();
  await workbook.xlsx.load(file);
  const sheetRows = sheetToJson(workbook.worksheets[0], cellToPrimitiveKeepDates);
  const changedRows: GoalRow[] = [];
  let rowsSoFar = existingRows;
  const seen = new Set<string>();
  sheetRows.forEach((sheetRow, index) => {
    // +2: one for the header row, one because spreadsheet rows are 1-indexed.
    const rowNumber = index + 2;
    const parsedRow = mapImportRow(sheetRow, company, customSegments);
    const existing = parsedRow.id == null ? undefined : existingById.get(parsedRow.id);
    // Ad Group (Platform) is read-only (the lambda never saves it): an import never changes it.
    const newRow = { ...parsedRow, ad_group_platform: existing?.ad_group_platform };
    const key = rowKey(newRow, customSegments);
    if (seen.has(key)) {
      return;
    }
    seen.add(key);

    if (newRow.id != null && !existing) {
      throw new Error(`Row ${rowNumber}: unknown id "${sheetRow.id ?? sheetRow.ID}"`);
    }
    const { isValid, error } = validateGoal(newRow, rowsSoFar, customSegments, selectorOptions);
    if (!isValid) {
      throw new Error(`Row ${rowNumber}: ${error}`);
    }
    if (!existing) {
      changedRows.push(newRow);
      rowsSoFar = [...rowsSoFar, newRow];
    } else if (rowIsDiff(existing, newRow, customSegments)) {
      changedRows.push(newRow);
      rowsSoFar = rowsSoFar.map(row => (row.id === newRow.id ? newRow : row));
    }
  });
  return changedRows;
};

export const channelsOf = (row: GoalRow): string[] =>
  Array.isArray(row.Channel) ? row.Channel.map(String) : [];

export const intersects = (a: GoalRow, b: GoalRow): boolean =>
  !!a.start_date &&
  !!a.end_date &&
  !!b.start_date &&
  !!b.end_date &&
  a.start_date <= b.end_date &&
  b.start_date <= a.end_date;

// A saved row conflicts with a legacy week when it covers any of its days for the same goal and
// shares a Channel.
export const isLegacyConflict = (legacy: GoalRow, other: GoalRow): boolean =>
  other.goal_name === legacy.goal_name &&
  intersects(legacy, other) &&
  channelsOf(other).some(channel => channelsOf(legacy).includes(channel));

// A legacy row as /update_expected_bookings takes it. Bookings there are whole dollars, as on the
// old Expected Bookings page (parseInt(value) || 0).
export const toLegacyBooking = (row: GoalRow): ExpectedBooking => ({
  id: row.legacy_id ?? 0,
  week: row.start_date ?? '',
  company: row.company ?? '',
  media: row.legacy_media ?? '',
  booking: Math.round(row.goal_value ?? 0)
});

// A budget that puts pacing-page channels together with channels that have none.
export const mixesPacingChannels = (row: GoalRow): boolean => {
  const channels = channelsOf(row);
  return channels.some(c => PACING_CHANNELS.has(c)) && channels.some(c => !PACING_CHANNELS.has(c));
};

// Whether `channel` can no longer be picked: once a row has pacing channels only the other pacing
// channels are open, and once it has other channels the pacing ones are closed. A row that
// already mixes the two stays fully open so it can be fixed.
export const isChannelOptionDisabled = (selected: string[], channel: string): boolean => {
  const hasPacing = selected.some(c => PACING_CHANNELS.has(c));
  const hasOther = selected.some(c => !PACING_CHANNELS.has(c));
  if (hasPacing === hasOther) {
    return false;
  }
  return hasPacing !== PACING_CHANNELS.has(channel);
};
